import { useQuery } from "@tanstack/react-query";

import { IJupyterRegistration } from "../../../@types/admin/AdminJupyterRegistration";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { jupyterService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/**
 * Every JupyterHealth Exchange connection for the admin page (EN-5149).
 * The response embeds all scopes, surveys and outbox messages, so this single
 * query feeds the list and every drill-down.
 */
export function useJupyterRegistrations(
  options: { refetchOnMount?: boolean } = {}
) {
  const { data, isLoading, isError, isFetching, error, refetch } = useQuery<
    IPaginatedResponse<IJupyterRegistration[]>
  >({
    queryKey: ["admin-jupyter-registrations"],
    queryFn: async () => ensureOk(await jupyterService.getRegistrations()),
    // The global QueryClient disables refetchOnMount/focus with a 3-minute
    // staleTime (config/query.ts) and migrated admin pages carry no manual
    // Refresh button (EN-5129 UI feedback), so force a fresh read each visit.
    // The drill-down modal shares this query for live statuses and opts out,
    // so opening it does not fire a second request.
    refetchOnMount: options.refetchOnMount === false ? false : "always",
  });

  return {
    registrations: data?.result ?? [],
    isLoading,
    isError,
    isFetching,
    error,
    refetch,
  };
}
